/**
 * The widget's sizes (plan 12 §8): the widget sizes its frame type allows,
 * or any size for a type sized freely (`widget-sizing.ts`). An edge follows
 * the hand while it drags; on release the widget snaps to the nearest
 * allowed size, keeping the corner opposite the dragged edge still.
 */
import type { Window as TauriWindow } from "@tauri-apps/api/window";
import {
  WIDGET_SIZE_ORDER,
  WIDGET_SIZES,
  type WidgetSize,
} from "@/components/shapes/shared/widget-face";
import {
  FREE_WIDGET_MIN,
  type PinnedWidgetSize,
  type WidgetSizing,
} from "@/components/shapes/shared/widget-sizing";
import type { PinRect } from "./desktop-pin-contract";

export type ResizeDirection = Parameters<TauriWindow["startResizeDragging"]>[0];

export function isWidgetSize(value: unknown): value is WidgetSize {
  return WIDGET_SIZE_ORDER.includes(value as WidgetSize);
}

export function isPinnedWidgetSize(value: unknown): value is PinnedWidgetSize {
  return value === "free" || isWidgetSize(value);
}

/** Of `sizes` (all three by default), the one closest to `width` × `height`. */
export function nearestWidgetSize(
  { width, height }: { width: number; height: number },
  sizes: readonly WidgetSize[] = WIDGET_SIZE_ORDER,
): WidgetSize {
  let best: WidgetSize = sizes[0] ?? "small";
  let bestDistance = Infinity;
  for (const size of sizes) {
    const target = WIDGET_SIZES[size];
    const distance = Math.hypot(target.width - width, target.height - height);
    if (distance < bestDistance) {
      best = size;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * `rect` at `size`; free keeps its own size, no smaller than the widget
 * size `min`. An edge that moved away from `before` while its
 * opposite edge stayed was dragged, so the opposite edge stays put; without
 * a dragged edge (a move, a size picked from the menu) the top-left stays.
 */
export function pinRectAtSize(
  rect: PinRect,
  size: PinnedWidgetSize,
  before: PinRect | null = null,
  min: WidgetSize = "small",
): PinRect {
  const { width, height } =
    size === "free"
      ? {
          width: Math.max(WIDGET_SIZES[min].width, rect.width),
          height: Math.max(WIDGET_SIZES[min].height, rect.height),
        }
      : WIDGET_SIZES[size];
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;
  const keepRight =
    before !== null &&
    Math.abs(rect.x - before.x) >= 1 &&
    Math.abs(right - (before.x + before.width)) < 1;
  const keepBottom =
    before !== null &&
    Math.abs(rect.y - before.y) >= 1 &&
    Math.abs(bottom - (before.y + before.height)) < 1;
  return {
    x: keepRight ? right - width : rect.x,
    y: keepBottom ? bottom - height : rect.y,
    width,
    height,
  };
}

/**
 * Where a released move or resize snaps: the nearest size `sizing` allows,
 * or free at its own size; the dragged edge's opposite kept.
 */
export function snapPinRectToSize(
  measured: PinRect,
  before: PinRect | null,
  sizing: WidgetSizing,
): { rect: PinRect; size: PinnedWidgetSize } {
  if (sizing.sizes === "free") {
    return {
      rect: pinRectAtSize(measured, "free", before, sizing.min),
      size: "free",
    };
  }
  const size = nearestWidgetSize(measured, sizing.sizes);
  return { rect: pinRectAtSize(measured, size, before), size };
}

/**
 * The rect after the pointer moved `dx`/`dy` from where a resize from
 * `direction` began, for the platforms that cannot start a native resize:
 * the dragged edges follow, the opposite ones stay, and the widget never
 * gets smaller than the small size (`FREE_WIDGET_MIN`).
 */
export function resizedPinRect(
  start: PinRect,
  direction: ResizeDirection,
  dx: number,
  dy: number,
): PinRect {
  const min = FREE_WIDGET_MIN;
  let { x, y, width, height } = start;
  if (direction.includes("East")) {
    width = Math.max(min.width, start.width + dx);
  }
  if (direction.includes("West")) {
    width = Math.max(min.width, start.width - dx);
    x = start.x + start.width - width;
  }
  if (direction.includes("South")) {
    height = Math.max(min.height, start.height + dy);
  }
  if (direction.includes("North")) {
    height = Math.max(min.height, start.height - dy);
    y = start.y + start.height - height;
  }
  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(width),
    height: Math.round(height),
  };
}
